import Box from '@mui/material/Box';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { useTheme, type SxProps, type Theme } from '@mui/material/styles';
import InfoOutlined from '@mui/icons-material/InfoOutlined';

export const VAULT_TOOLTIPS = {
  gmailQueryTerms: {
    text: 'The search results only show messages that exactly match all of the words and phrases in your query.',
    linkHref: 'https://support.google.com/vault?p=gmail_search&hl=en',
    linkText: 'Learn more about Gmail search terms',
  },
  groupsQueryTerms: {
    text: 'The search results only show messages that exactly match all of the words and phrases in your query.',
    linkHref: 'https://support.google.com/vault/answer/2539209?hl=en',
    linkText: 'Learn more about Google Groups search terms',
  },
  calendarEventVersioning: {
    text: 'When you enter a version date, Vault returns the latest available version before 12:00 AM UTC on that date. To get the full set of events based on their status on that date, don’t enter filters. Vault applies filters to the current event versions, then returns versioned data for these events.',
    linkHref:
      'https://support.google.com/vault?p=calendar_vault_searching_versions&hl=en',
    linkText: 'Learn more about event versioning',
  },
  calendarQueryTerms: {
    text: 'The search results only show events that exactly match all of the words and phrases in your query.',
    linkHref: 'https://support.google.com/vault?p=calendar_search&hl=en',
    linkText: 'Learn more about Calendar search terms',
  },
  calendarResponseStatus: {
    text: 'Filter by response from guests in the Entity field. To view events with any response, with or without guests, uncheck all statuses.',
    linkHref:
      'https://support.google.com/vault/answer/12568582?hl=en&visit_id=639219761617561996-829609231&p=calendar_search_attendee_responses&rd=1#response',
    linkText: 'Learn about response status filter',
  },
  chatIncludeSpaces: {
    text: 'When turned on, Vault also searches spaces and group chats where the searched account is or was a member.',
    linkHref: 'https://support.google.com/vault?p=hangouts_search_start&hl=en',
    linkText: 'Learn more',
  },
  vaultTimezone: {
    text: 'The time zone you choose can change search results.',
    linkHref: 'https://support.google.com/vault?p=vault_timezones&hl=en',
    linkText: 'Learn more',
  },
  vaultQueryTerms: {
    text: 'Use terms and operators to refine your search.',
    linkHref: 'https://support.google.com/vault?p=vault_search_operators&hl=en',
    linkText: 'Learn more',
  },
  driveVersionDate: {
    text: 'When you enter a version date, Vault searches the current version of supported files, but you preview and export the contents of the last version saved before 12:00 AM UTC on the date.',
    linkHref:
      'https://support.google.com/vault?p=vault_searching_versions&hl=en',
    linkText: 'Learn more',
  },
  heldData: {
    text: 'Held data searches use a fixed time zone.',
    linkHref: 'https://support.google.com/vault?p=vault_timezones&hl=en',
    linkText: 'Learn more',
  },
  dateModified: {
    text: 'Search for items last modified in the date range.',
    linkHref:
      'https://support.google.com/vault?p=drive_search_timestamps&hl=en',
    linkText: 'Learn more',
  },
  termsOptional: {
    text: 'Use terms and operators to refine your search.',
    linkHref: 'https://support.google.com/vault?p=vault_search_operators&hl=en',
    linkText: 'Learn more',
  },
  dataRegion: {
    text: 'Choose a location for your export data.',
    linkHref: 'https://support.google.com/vault?p=export_location&hl=en',
    linkText: 'Learn more',
  },
  indirectAccess: {
    text: 'A user has indirect access when a Drive item is shared publicly, with a group, or with an organization containing that user’s account. Enabling this setting might increase export time.',
    linkHref:
      'https://support.google.com/vault?p=vault_export_indirect_access&hl=en',
    linkText: 'Learn more',
  },
  sharedDrives: {
    title: 'Shared Drives',
    subtext: 'Determine which shared drives are included in the export.',
    text: (
      <>
        <b>Not Included</b>
        <br />
        If a resource is in a shared drive, it isn't included in the search
        results.
        <br />
        <br />
        <b>
          Included only if documents shared directly (not due to shared drive
          membership)
        </b>
        <br />
        <i style={{ opacity: 0.8, display: 'block', margin: '2px 0' }}>
          (Previously "include items from shared drives" toggle off)
        </i>
        Shared drive resources are only included in instances where the account
        is a collaborator on a resource but they are not a member of the shared
        drive.
        <br />
        <br />
        <b>Included</b>
        <br />
        <i style={{ opacity: 0.8, display: 'block', margin: '2px 0' }}>
          (Previously "include items from shared drives" toggle on)
        </i>
        Shared drive resources are included in instances where the account is a
        collaborator on a resource or if they are a member of the shared drive.
      </>
    ) as React.ReactNode,
    linkHref: 'https://support.google.com/vault?p=vaultsearchfiles&hl=en',
    linkText: 'Learn more',
  },
};

export type VaultTooltipPreset = keyof typeof VAULT_TOOLTIPS;

interface VaultTooltipWithLinkProps {
  preset?: VaultTooltipPreset;
  title?: string;
  subtext?: string;
  text?: React.ReactNode;
  linkHref?: string;
  linkText?: string;
  sxIcon?: SxProps<Theme>;
}

export default function VaultTooltipWithLink({
  preset,
  title,
  subtext,
  text,
  linkHref,
  linkText,
  sxIcon,
}: VaultTooltipWithLinkProps) {
  const theme = useTheme();

  const tooltipConfig: {
    title?: string;
    subtext?: string;
    text?: React.ReactNode;
    linkHref?: string;
    linkText?: string;
  } = preset
    ? (VAULT_TOOLTIPS[preset] as any)
    : { title, subtext, text, linkHref, linkText };

  if (
    !tooltipConfig.text ||
    !tooltipConfig.linkHref ||
    !tooltipConfig.linkText
  ) {
    return null;
  }

  return (
    <Tooltip
      title={
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          {tooltipConfig.title && (
            <Typography
              variant="caption"
              sx={{ fontWeight: 'bold', display: 'block' }}
            >
              {tooltipConfig.title}
            </Typography>
          )}
          {tooltipConfig.subtext && (
            <Typography
              variant="caption"
              sx={{ fontStyle: 'italic', display: 'block' }}
            >
              {tooltipConfig.subtext}
            </Typography>
          )}
          {tooltipConfig.text && (
            <Typography
              variant="caption"
              sx={{ display: 'block', whiteSpace: 'pre-line' }}
            >
              {tooltipConfig.text}
            </Typography>
          )}
          <a
            href={tooltipConfig.linkHref}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              color: (theme.palette as any).colors?.primary?.[300],
              textDecoration: 'underline',
              marginTop: '4px',
              display: 'block',
            }}
          >
            {tooltipConfig.linkText}
          </a>
        </Box>
      }
      arrow
    >
      <InfoOutlined
        fontSize="small"
        sx={{ color: theme.palette.semantic.icon.default, ...sxIcon }}
      />
    </Tooltip>
  );
}
